/**
 * UI-8a Task 2 — useTenants hook.
 *
 * Composes `useAdminResource('/tenants', { responseKey: 'tenants' })` for the
 * list and base CRUD verbs, plus two tenant-lifecycle helpers that fall outside
 * the standard CRUD envelope:
 *
 *   - provision(id)               → POST /tenants/:id/provision
 *   - provisionAdminUser(id, …)   → POST /tenants/:id/admin-user
 *
 * The provisioning wizard (ProvisionWizardDrawer) drives these in sequence:
 *   1. create({ slug, name, planId? })  → tenant row
 *   2. provision(tenant.id)             → provisions the per-tenant schema
 *   3. provisionAdminUser(tenant.id, …) → seeds the first TENANT_ADMIN user
 *
 * All routes are gated by PLATFORM_TENANTS_MANAGE on the backend; the hook
 * itself imposes no client-side ACL — pages render a 403 / nav guard upstream.
 */
import { useCallback } from 'react'
import { api } from '../api/client.js'
import { useAdminResource } from './useAdminApi.js'

export interface Tenant {
  id: string
  slug: string
  name: string
  /** Legal entity name used for TCPA one-to-one signer matching (S5 T3). */
  legalName: string | null
  planId: string | null
  status: 'active' | 'suspended'
  schemaProvisioned: boolean
  provisionedAt: string | null
  featureFlags: Record<string, unknown>
  createdAt: string
  // Basic Info configuration
  domainUrl: string | null
  timeZone: string | null
  userLimitation: string | null
  // Client Details
  clientFullName: string | null
  clientEmail: string | null
  clientMobileNumber: string | null
  clientLandline: string | null
  clientAddress: string | null
  // Company Details
  companyName: string | null
  companyEmail: string | null
  // AWS S3
  enableS3: boolean | null
  awss3BucketName: string | null
  awss3AccessKey: string | null
  awss3Secret: string | null
  awss3Region: string | null
  awss3UrlExpiry: number | null
  // Post-call analytics (AWS Transcribe). The secret itself is never returned.
  postCallAnalytics?: boolean
  awsTranscribeAccessKey?: string | null
  awsTranscribeSecretConfigured?: boolean
  awsTranscribeRegion?: string | null
  awsTranscribeS3Bucket?: string | null
  awsTranscribeLanguageCode?: string | null
  awsTranscribeIamArn?: string | null
  manualCallHistoryUpload?: boolean
  showAgentSentiment?: boolean
  // Live call assist engine. The Whisper key itself is never returned.
  liveAssistEngine?: 'aws' | 'self_hosted'
  liveAssistWhisperUrl?: string | null
  liveAssistWhisperKeyConfigured?: boolean
  liveAssistModel?: string | null
  // Company type and default seed toggle
  companyType: string | null
  seedDefaultData: boolean | null
  /** Super Admin recording settings; null/absent = never configured. */
  recordingConfig?: (import('@kc3/contracts').TenantRecordingConfigInput & {
    tenantId: string
    updatedAt: string | null
  }) | null
}

/** Write-side transcription fields; `awsTranscribeSecret` omitted = keep stored. */
interface TranscriptionBody {
  postCallAnalytics?: boolean
  awsTranscribeAccessKey?: string | null
  awsTranscribeSecret?: string
  awsTranscribeRegion?: string | null
  awsTranscribeS3Bucket?: string | null
  awsTranscribeLanguageCode?: string | null
  awsTranscribeIamArn?: string | null
  manualCallHistoryUpload?: boolean
  showAgentSentiment?: boolean
  liveAssistEngine?: 'aws' | 'self_hosted'
  liveAssistWhisperUrl?: string | null
  /** Omitted = keep the stored key. */
  liveAssistWhisperKey?: string
  liveAssistModel?: string | null
}

export interface CreateTenantBody extends TranscriptionBody {
  slug: string
  name: string
  planId?: string
  status?: 'active' | 'suspended'
  legalName?: string | null
  // Basic Info configuration
  domainUrl?: string
  timeZone?: string
  userLimitation?: string
  // Client Details
  clientFullName?: string
  clientEmail?: string
  clientMobileNumber?: string
  clientLandline?: string
  clientAddress?: string
  // Company Details
  companyName?: string
  companyEmail?: string
  // AWS S3
  enableS3?: boolean
  awss3BucketName?: string
  awss3AccessKey?: string
  awss3Secret?: string
  awss3Region?: string
  awss3UrlExpiry?: number
  // Feature flags
  featureFlags?: Record<string, boolean>
  // Company type and default seed toggle
  companyType?: string
  seedDefaultData?: boolean
}

export type UpdateTenantBody = Partial<{
  name: string
  status: string
  planId: string
  /** Legal entity name for TCPA one-to-one consent matching (S5 T3). */
  legalName: string | null
  // Basic Info configuration
  domainUrl: string
  timeZone: string
  userLimitation: string
  // Client Details
  clientFullName: string
  clientEmail: string
  clientMobileNumber: string
  clientLandline: string
  clientAddress: string
  // Company Details
  companyName: string
  companyEmail: string
  // AWS S3
  enableS3: boolean
  awss3BucketName: string
  awss3AccessKey: string
  awss3Secret: string
  awss3Region: string
  awss3UrlExpiry: number
  // Feature flags
  featureFlags: Record<string, unknown>
  // Company type and default seed toggle
  companyType: string
  seedDefaultData: boolean
}> & TranscriptionBody

export interface ProvisionAdminUserPayload {
  email: string
  fullName: string
  temporaryPassword: string
}

export interface UseTenantsResult {
  items: Tenant[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  create: (body: CreateTenantBody) => Promise<Tenant>
  update: (id: string, body: UpdateTenantBody) => Promise<Tenant>
  /** POST /tenants/:id/provision — provisions the per-tenant schema. */
  provision: (id: string) => Promise<void>
  /** POST /tenants/:id/admin-user — seeds the first TENANT_ADMIN user. */
  provisionAdminUser: (
    id: string,
    payload: ProvisionAdminUserPayload,
  ) => Promise<{ userId: string }>
  /** DELETE /tenants/:id — deletes tenant and drops its schema. */
  deleteItem: (id: string) => Promise<void>
}

const BASE_PATH = '/tenants'

export interface UseTenantsFilters {
  status?: string
}

export function useTenants(filters?: UseTenantsFilters): UseTenantsResult {
  const base = useAdminResource<Tenant>(BASE_PATH, {
    responseKey: 'tenants',
    ...(filters ? { query: filters as Record<string, string | undefined> } : {}),
  })

  // useAdminResource.refetch() is fire-and-forget (bumps a fetchKey); wrap so
  // callers can `await` after a mutation. List state updates via the effect.
  const refetch = useCallback(async (): Promise<void> => {
    base.refetch()
  }, [base])

  const create = useCallback(
    async (body: CreateTenantBody): Promise<Tenant> => {
      const created = await api.post<Tenant>(BASE_PATH, body)
      base.refetch()
      return created
    },
    [base],
  )

  const update = useCallback(
    async (id: string, body: UpdateTenantBody): Promise<Tenant> => {
      const updated = await api.patch<Tenant>(`${BASE_PATH}/${id}`, body)
      base.refetch()
      return updated
    },
    [base],
  )

  const provision = useCallback(async (id: string): Promise<void> => {
    await api.post(`${BASE_PATH}/${id}/provision`, {})
  }, [])

  const provisionAdminUser = useCallback(
    async (
      id: string,
      payload: ProvisionAdminUserPayload,
    ): Promise<{ userId: string }> => {
      return api.post<{ userId: string }>(
        `${BASE_PATH}/${id}/admin-user`,
        payload,
      )
    },
    [],
  )

  const deleteItem = useCallback(async (id: string): Promise<void> => {
    await api.delete(`${BASE_PATH}/${id}`)
    base.refetch()
  }, [base])

  return {
    items: base.items,
    loading: base.loading,
    error: base.error,
    refetch,
    create,
    update,
    provision,
    provisionAdminUser,
    deleteItem,
  }
}
